// === IMPORTS ===
import React, { useState, useEffect, useRef } from 'react';
import { OfficeLocation, LocationType, IsoTileType, SystemTimezone } from '../types';
import {
  Building2,
  Plus,
  Trash2,
  Copy,
  Edit2,
  Check,
  ArrowRightLeft,
  Layers,
  Users,
  Grid,
  Box,
  Clock,
  Globe,
  X,
  Info,
  CheckCircle2,
} from 'lucide-react';
import { PRESET_LOCATION_TEMPLATES, DEFAULT_STARTUP_AGENTS } from '../data/defaultLocations';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { POPULAR_IANA_TIMEZONES, getTimezoneOffsetDisplay } from '../utils/timezoneUtils';
import './LocationManagerDrawer.css';

export interface LocationManagerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  locations: OfficeLocation[];
  activeLocationId: string;
  onSelectLocation: (locationId: string) => void;
  onCreateLocation: (location: OfficeLocation) => void;
  onUpdateLocation: (location: OfficeLocation) => void;
  onDeleteLocation: (locationId: string) => void;
  onDuplicateLocation: (locationId: string) => void;
  onTransferAgent: (agentId: string, fromLocationId: string, toLocationId: string) => void;
  systemTimezones?: SystemTimezone[];
  defaultTimezone?: string;
  onEnsureTimezoneRegistered?: (iana: string) => void;
  initialTab?: 'details' | 'list' | 'create' | 'transfer';
}

export const LOCATION_TYPE_OPTIONS: { id: LocationType; label: string; icon: string }[] = [
  { id: 'office', label: 'Office Floor', icon: '🏢' },
  { id: 'warehouse', label: 'Warehouse / Logistics Bay', icon: '📦' },
  { id: 'lounge', label: 'Lounge / Creative Space', icon: '🛋️' },
  { id: 'datacenter', label: 'Data Center / Servers', icon: '🖥️' },
  { id: 'floor', label: 'Floor / General Level', icon: '🏬' },
  { id: 'custom', label: 'Custom / Laboratory', icon: '🧪' },
];

// === COMPONENTE: DRAWER GESTOR DE UBICACIONES/PISOS (4 PESTAÑAS) ===
export const LocationManagerDrawer: React.FC<LocationManagerDrawerProps> = ({
  isOpen,
  onClose,
  locations,
  activeLocationId,
  onSelectLocation,
  onCreateLocation,
  onUpdateLocation,
  onDeleteLocation,
  onDuplicateLocation,
  onTransferAgent,
  defaultTimezone = 'Europe/Madrid',
  onEnsureTimezoneRegistered,
  initialTab = 'details',
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'list' | 'create' | 'transfer'>(initialTab);
  const [inspectedLocationId, setInspectedLocationId] = useState<string>(activeLocationId);
  const [successToast, setSuccessToast] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      setInspectedLocationId(activeLocationId);
    }
  }, [isOpen, initialTab, activeLocationId]);

  // === CERRAR CON CLIC AFUERA O TECLA ESCAPE ===
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: MouseEvent | TouchEvent | PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      if (drawerRef.current && drawerRef.current.contains(target)) {
        return;
      }

      if (target.closest('#confirm-delete-modal-overlay') || target.closest('#confirm-delete-modal-container')) {
        return;
      }

      onClose();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const currentLoc = locations.find((l) => l.id === inspectedLocationId) || locations.find((l) => l.id === activeLocationId) || locations[0];

  const [editName, setEditName] = useState<string>(currentLoc?.name || '');
  const [editIcon, setEditIcon] = useState<string>(currentLoc?.icon || '🏢');
  const [editType, setEditType] = useState<LocationType>(currentLoc?.locationType || 'office');
  const [editDesc, setEditDesc] = useState<string>(currentLoc?.description || '');
  const [editTimezone, setEditTimezone] = useState<string>(currentLoc?.timezone || defaultTimezone || 'Europe/Madrid');
  const [isEditingDetails, setIsEditingDetails] = useState<boolean>(false);

  useEffect(() => {
    if (currentLoc) {
      setEditName(currentLoc.name);
      setEditIcon(currentLoc.icon || '🏢');
      setEditType(currentLoc.locationType || 'office');
      setEditDesc(currentLoc.description || '');
      setEditTimezone(currentLoc.timezone || defaultTimezone || 'Europe/Madrid');
      setIsEditingDetails(false);
    }
  }, [currentLoc?.id, currentLoc?.name, currentLoc?.icon, currentLoc?.locationType, currentLoc?.description, currentLoc?.timezone, defaultTimezone]);

  const [newName, setNewName] = useState<string>('New Floor');
  const [newIcon, setNewIcon] = useState<string>('🏢');
  const [newType, setNewType] = useState<LocationType>('office');
  const [newWidth, setNewWidth] = useState<number>(20);
  const [newHeight, setNewHeight] = useState<number>(20);
  const [newFloorType, setNewFloorType] = useState<IsoTileType>('wood_parquet');
  const [newDesc, setNewDesc] = useState<string>('');
  const [newTimezone, setNewTimezone] = useState<string>(defaultTimezone || 'Europe/Madrid');
  const [templateChoice, setTemplateChoice] = useState<'empty' | 'office' | 'warehouse' | 'lounge'>('office');

  const [transferAgentId, setTransferAgentId] = useState<string>('');
  const [transferFromLocId, setTransferFromLocId] = useState<string>(activeLocationId);
  const [transferToLocId, setTransferToLocId] = useState<string>('');

  const [locationToDelete, setLocationToDelete] = useState<OfficeLocation | null>(null);

  const showToast = (msg: string) => {
    setSuccessToast(msg);
    setTimeout(() => setSuccessToast(null), 3000);
  };

  const handleSaveDetails = () => {
    if (!currentLoc) return;

    if (onEnsureTimezoneRegistered && editTimezone) {
      onEnsureTimezoneRegistered(editTimezone);
    }

    onUpdateLocation({
      ...currentLoc,
      name: editName.trim() || currentLoc.name,
      icon: editIcon || '🏢',
      locationType: editType,
      description: editDesc.trim() || undefined,
      timezone: editTimezone || 'Europe/Madrid',
    });
    setIsEditingDetails(false);
    showToast(`Floor "${editName.trim() || currentLoc.name}" updated successfully`);
  };

  const handleCreate = () => {
    const id = `loc_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

    if (onEnsureTimezoneRegistered && newTimezone) {
      onEnsureTimezoneRegistered(newTimezone);
    }

    let baseLocation: OfficeLocation;

    if (templateChoice === 'office') {
      const template = PRESET_LOCATION_TEMPLATES.find((l) => l.id === 'loc_template_office') || PRESET_LOCATION_TEMPLATES[0];
      baseLocation = {
        ...template,
        id,
        name: newName.trim() || 'Office Floor',
        icon: newIcon || '🏢',
        locationType: newType,
        description: newDesc.trim() || template.description,
        timezone: newTimezone || 'Europe/Madrid',
        agents: template.agents.map((ag) => ({ ...ag, id: `${ag.id}_${Date.now()}` })),
      };
    } else if (templateChoice === 'warehouse') {
      const template = PRESET_LOCATION_TEMPLATES.find((l) => l.id === 'loc_template_warehouse') || PRESET_LOCATION_TEMPLATES[1] || PRESET_LOCATION_TEMPLATES[0];
      baseLocation = {
        ...template,
        id,
        name: newName.trim() || 'Logistics Warehouse Bay',
        icon: newIcon || '📦',
        locationType: newType,
        description: newDesc.trim() || template.description,
        timezone: newTimezone || 'Europe/Madrid',
        agents: template.agents.map((ag) => ({ ...ag, id: `${ag.id}_${Date.now()}` })),
      };
    } else if (templateChoice === 'lounge') {
      const template = PRESET_LOCATION_TEMPLATES.find((l) => l.id === 'loc_template_lounge') || PRESET_LOCATION_TEMPLATES[2] || PRESET_LOCATION_TEMPLATES[0];
      baseLocation = {
        ...template,
        id,
        name: newName.trim() || 'Lounge & Creative Space',
        icon: newIcon || '🛋️',
        locationType: newType,
        description: newDesc.trim() || template.description,
        timezone: newTimezone || 'Europe/Madrid',
        agents: template.agents.map((ag) => ({ ...ag, id: `${ag.id}_${Date.now()}` })),
      };
    } else {
      baseLocation = {
        id,
        name: newName.trim() || 'Empty Space',
        icon: newIcon || '🏢',
        locationType: newType,
        description: newDesc.trim() || 'Open layout floor ready to build.',
        gridWidth: Math.max(4, Math.min(50, newWidth)),
        gridHeight: Math.max(4, Math.min(50, newHeight)),
        floorType: newFloorType,
        customFloors: {},
        rooms: [],
        items: [],
        timezone: newTimezone || 'Europe/Madrid',
        agents: DEFAULT_STARTUP_AGENTS.map((ag) => ({ ...ag, id: `${ag.id}_${Date.now()}` })),
      };
    }

    onCreateLocation(baseLocation);
    onSelectLocation(baseLocation.id);
    setInspectedLocationId(baseLocation.id);
    setActiveTab('details');
    showToast(`Floor "${baseLocation.name}" created and opened successfully`);
  };

  const handleExecuteTransfer = () => {
    if (!transferAgentId || !transferFromLocId || !transferToLocId || transferFromLocId === transferToLocId) {
      return;
    }
    onTransferAgent(transferAgentId, transferFromLocId, transferToLocId);
    setTransferAgentId('');
    showToast('Agent transferred successfully');
  };

  const roomCount = currentLoc?.rooms?.length || 0;
  const itemCount = currentLoc?.items?.length || 0;
  const placedAgents = (currentLoc?.agents || []).filter(
    (ag) => ag.isPlaced === true || (ag.isPlaced !== false && ag.currentCol >= 0 && ag.currentRow >= 0)
  );
  const placedAgentCount = placedAgents.length;
  const totalAreaM2 = currentLoc
    ? ((currentLoc.gridWidth * (currentLoc.tileSizeMeters ?? 1.0)) * (currentLoc.gridHeight * (currentLoc.tileSizeMeters ?? 1.0))).toFixed(1)
    : '0';

  const sourceLocation = locations.find((l) => l.id === transferFromLocId);
  const sourceAgents = sourceLocation?.agents || [];

  // === RENDER ===
  return (
    <>
      <aside
        ref={drawerRef}
        id="location-manager-drawer"
        aria-label="Side Drawer: Floors and Locations Manager"
        className={`twin-location-drawer ${isOpen ? 'twin-location-drawer--open' : 'twin-location-drawer--closed'}`}
      >
        {/* === HEADER === */}
        
        <div className="twin-drawer-header">
          <div className="twin-drawer-header-left">
            <div className="twin-drawer-icon-badge twin-location-header-icon-wrap">
              <Building2 className="twin-icon-sm" />
            </div>
            <div className="twin-drawer-header-text">
              <h2 className="twin-drawer-title">
                <span>Floors &amp; Locations Manager</span>
                <span className="twin-location-header-count-badge">
                  {locations.length} {locations.length === 1 ? 'floor' : 'floors'}
                </span>
              </h2>
              <p className="twin-drawer-subtitle twin-drawer-subtitle--accent">
                {currentLoc ? `${currentLoc.icon || '🏢'} ${currentLoc.name} (${currentLoc.gridWidth}x${currentLoc.gridHeight})` : 'Spatial management'}
              </p>
            </div>
          </div>

          <button type="button" onClick={onClose} className="twin-drawer-close-btn" aria-label="Close floors panel" title="Close drawer (Esc)">
            <X className="twin-icon-sm" />
          </button>
        </div>

        {/* === NOTIFICACIÓN DE ÉXITO === */}
        {successToast && (
          <div className="twin-location-toast">
            <div className="twin-location-toast-left">
              <CheckCircle2 className="twin-icon-sm" />
              <span>{successToast}</span>
            </div>
            <button onClick={() => setSuccessToast(null)} className="twin-location-toast-close">
              <X className="twin-icon-xs" />
            </button>
          </div>
        )}

        {/* === PESTAÑAS === */}
        <div className="twin-location-tabs">
          <button type="button" onClick={() => setActiveTab('details')} className={`twin-location-tab ${activeTab === 'details' ? 'twin-location-tab--active-cyan' : ''}`}>
            <Info className="twin-icon-xs twin-color-theme-accent" />
            <span>Info &amp; Edit</span>
          </button>

          <button type="button" onClick={() => setActiveTab('list')} className={`twin-location-tab ${activeTab === 'list' ? 'twin-location-tab--active-cyan' : ''}`}>
            <Building2 className="twin-icon-xs twin-color-cyan" />
            <span>All ({locations.length})</span>
          </button>

          <button type="button" onClick={() => setActiveTab('create')} className={`twin-location-tab ${activeTab === 'create' ? 'twin-location-tab--active-emerald' : ''}`}>
            <Plus className="twin-icon-xs twin-color-emerald" />
            <span>New Floor</span>
          </button>

          <button type="button" onClick={() => setActiveTab('transfer')} className={`twin-location-tab ${activeTab === 'transfer' ? 'twin-location-tab--active-purple' : ''}`}>
            <ArrowRightLeft className="twin-icon-xs twin-color-emerald" />
            <span>Transfer</span>
          </button>
        </div>

        {/* === CUERPO === */}
        <div className="twin-location-body">
          {/* === TAB 1: DETALLES Y EDICIÓN === */}
          {activeTab === 'details' && currentLoc && (
            <div className="twin-location-section">
              <div className="twin-location-header-card">
                <div className="twin-location-header-card-left">
                  <div className="twin-location-header-card-icon">{currentLoc.icon || '🏢'}</div>
                  <div className="twin-location-header-card-text">
                    <div className="twin-location-header-card-name-row">
                      <h3 className="twin-location-header-card-name">{currentLoc.name}</h3>
                      {currentLoc.id === activeLocationId ? (
                        <span className="twin-location-active-badge">Active Canvas</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            onSelectLocation(currentLoc.id);
                            showToast(`Switched to floor "${currentLoc.name}"`);
                          }}
                          className="twin-location-make-active-btn"
                        >
                          Make Active
                        </button>
                      )}
                    </div>
                    <p className="twin-location-header-card-desc">
                      {currentLoc.description || `${currentLoc.gridWidth}x${currentLoc.gridHeight} tiles (${totalAreaM2} m²)`}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsEditingDetails((prev) => !prev)}
                  className={`twin-location-edit-toggle-btn ${isEditingDetails ? 'twin-location-edit-toggle-btn--editing' : ''}`}
                >
                   <Edit2 className="twin-icon-xs" />
                  <span>{isEditingDetails ? 'View Metrics' : 'Edit Data'}</span>
                </button>
              </div>

              {isEditingDetails ? (
                <div className="twin-location-edit-card">
                  <div className="twin-location-edit-header">
                    <span className="twin-location-edit-title">
                       <Edit2 className="twin-icon-xs twin-color-theme-accent" />
                      <span>Edit Floor Configuration</span>
                    </span>
                    <span className="twin-location-edit-id">ID: {currentLoc.id}</span>
                  </div>

                  <div className="twin-location-edit-grid4">
                    <div className="twin-location-edit-field-name">
                      <label className="twin-location-edit-field-label">Floor Name:</label>
                      <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="e.g. Floor 1 - Engineering" className="twin-location-edit-input" />
                    </div>
                    <div>
                      <label className="twin-location-edit-field-label">Icon / Emoji:</label>
                      <input type="text" value={editIcon} onChange={(e) => setEditIcon(e.target.value)} className="twin-location-edit-icon-input" maxLength={4} />
                    </div>
                  </div>

                  <div className="twin-location-edit-grid2">
                    <div>
                      <label className="twin-location-edit-field-label">Space Type:</label>
                      <select value={editType} onChange={(e) => setEditType(e.target.value as LocationType)} className="twin-location-edit-select">
                        {LOCATION_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.icon} {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="twin-location-edit-field-label">Short Description:</label>
                      <input type="text" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder="Description or purpose..." className="twin-location-edit-input" />
                    </div>
                  </div>

                  <div className="twin-location-tz-field">
                    <label className="twin-location-tz-label-row">
                      <span className="twin-location-tz-label">
                        <Clock className="twin-icon-xxs twin-color-cyan" />
                        <span>Floor / Facility Timezone:</span>
                      </span>
                      <span className="twin-location-tz-offset">{getTimezoneOffsetDisplay(editTimezone)}</span>
                    </label>
                    <select value={editTimezone} onChange={(e) => setEditTimezone(e.target.value)} className="twin-location-tz-select">
                      {POPULAR_IANA_TIMEZONES.map((tz) => (
                        <option key={tz.iana} value={tz.iana}>
                          {tz.flag} {tz.name} ({getTimezoneOffsetDisplay(tz.iana)})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="twin-location-edit-footer">
                    <button type="button" onClick={() => setIsEditingDetails(false)} className="twin-location-cancel-btn">
                      Cancel
                    </button>
                    <button type="button" onClick={handleSaveDetails} className="twin-location-save-btn">
                      <Check className="twin-icon-xs" />
                      <span>Save Changes</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="twin-location-section">
                  <div className="twin-location-metrics-grid">
                    <div className="twin-location-metric-card">
                      <div className="twin-location-metric-header">
                        <Grid className="twin-icon-xxs twin-color-cyan" />
                        <span>Dimensions</span>
                      </div>
                      <div className="twin-location-metric-value">
                        {currentLoc.gridWidth}x{currentLoc.gridHeight}
                      </div>
                      <div className="twin-location-metric-sub twin-location-metric-sub--cyan">{totalAreaM2} m²</div>
                    </div>

                    <div className="twin-location-metric-card">
                      <div className="twin-location-metric-header">
                        <Layers className="twin-icon-xxs twin-color-theme-accent" />
                        <span>Rooms / Zones</span>
                      </div>
                      <div className="twin-location-metric-value">
                        {roomCount} {roomCount === 1 ? 'zone' : 'zones'}
                      </div>
                      <div className="twin-location-metric-sub twin-location-metric-sub--amber">
                        {currentLoc.rooms?.map((r) => r.name).slice(0, 2).join(', ') || 'No zones'}
                      </div>
                    </div>

                    <div className="twin-location-metric-card">
                      <div className="twin-location-metric-header">
                        <Box className="twin-icon-xxs twin-color-theme-accent" />                        
                        <span>Objects</span>
                      </div>
                      <div className="twin-location-metric-value">
                        {itemCount} {itemCount === 1 ? 'object' : 'objects'}
                      </div>
                      <div className="twin-location-metric-sub twin-location-metric-sub--blue">Placed furniture</div>
                    </div>

                    <div className="twin-location-metric-card">
                      <div className="twin-location-metric-header">
                        <Users className="twin-icon-xxs twin-color-emerald" />
                        <span>AI Agents</span>
                      </div>
                      <div className="twin-location-metric-value twin-location-metric-value--emerald">{placedAgentCount} active</div>
                      <div className="twin-location-metric-sub">of {currentLoc.agents?.length || 0} assigned</div>
                    </div>
                  </div>

                  <div className="twin-location-tz-info-box">
                    <div className="twin-location-tz-info-left">
                      <div className="twin-location-tz-info-icon">
                        <Globe className="twin-icon-sm" />
                      </div>
                      <div className="twin-location-tz-info-text">
                        <div className="twin-location-tz-info-label">
                          <span>Floor Timezone:</span>
                          <span className="twin-location-tz-info-value">{currentLoc.timezone || 'Europe/Madrid'}</span>
                        </div>
                        <div className="twin-location-tz-info-desc">Local time and sync with the DVR Time Machine system</div>
                      </div>
                    </div>
                    <span className="twin-location-tz-offset-badge">{getTimezoneOffsetDisplay(currentLoc.timezone || 'Europe/Madrid')}</span>
                  </div>

                  {currentLoc.rooms && currentLoc.rooms.length > 0 && (
                    <div className="twin-location-list-card">
                      <div className="twin-location-list-card-header">
                        <span className="twin-location-list-card-title">
                          <Layers className="twin-icon-xs twin-color-theme-accent" />
                          <span>Zones defined on this floor ({currentLoc.rooms.length}):</span>
                        </span>
                      </div>
                      <div className="twin-location-list-grid">
                        {currentLoc.rooms.map((room) => (
                          <div key={room.id} className="twin-location-list-item">
                            <span className="twin-location-list-item-dot" style={{ backgroundColor: room.color || '#3b82f6' }} />
                            <div className="twin-location-list-item-text">
                              <p className="twin-location-list-item-name">{room.name}</p>
                              <p className="twin-location-list-item-sub">
                                {room.width}x{room.height} ({((room.width * (currentLoc.tileSizeMeters ?? 1.0)) * (room.height * (currentLoc.tileSizeMeters ?? 1.0))).toFixed(0)} m²)
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {placedAgents.length > 0 && (
                    <div className="twin-location-list-card">
                      <div className="twin-location-list-card-header">
                        <span className="twin-location-list-card-title">
                          <Users className="twin-icon-xs twin-color-emerald" />
                          <span>Autonomous agents present ({placedAgents.length}):</span>
                        </span>
                      </div>
                      <div className="twin-location-list-grid">
                        {placedAgents.map((ag) => (
                          <div key={ag.id} className="twin-location-list-item">
                            <div className="twin-location-agent-avatar-circle" style={{ backgroundColor: ag.avatarShirtColor || '#3b82f6' }}>
                              {ag.name.charAt(0)}
                            </div>
                            <div className="twin-location-list-item-text">
                              <p className="twin-location-list-item-name">{ag.name}</p>
                              <p className="twin-location-list-item-sub twin-location-list-item-sub--upper">{ag.role}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="twin-location-actions-footer">
                <div className="twin-location-actions-left">
                  <button
                    type="button"
                    onClick={() => {
                      onDuplicateLocation(currentLoc.id);
                      showToast(`Floor "${currentLoc.name}" duplicated successfully`);
                    }}
                    className="twin-location-action-btn"
                    title="Create an identical copy of this floor"
                  >
                    <Copy className="twin-icon-xs twin-color-cyan" />
                    <span>Duplicate Floor</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setTransferFromLocId(currentLoc.id);
                      setActiveTab('transfer');
                    }}
                    className="twin-location-action-btn twin-location-action-btn--purple"
                    title="Move agents from this floor to another"
                  >
                    <ArrowRightLeft className="twin-icon-xs twin-color-cyan" />
                    <span>Transfer Agents</span>
                  </button>
                </div>

                {locations.length > 1 && (
                  <button type="button" onClick={() => setLocationToDelete(currentLoc)} className="twin-location-delete-btn" title="Delete this floor">
                    <Trash2 className="twin-icon-xs" />
                    <span>Delete Floor</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* === TAB 2: LISTA DE TODAS LAS UBICACIONES === */}
          {activeTab === 'list' && (
            <div className="twin-location-section">
              <div className="twin-location-list-header-row">
                <span>Select a floor to open it or view its details:</span>
                <button type="button" onClick={() => setActiveTab('create')} className="twin-location-list-new-btn">
                  <Plus className="twin-icon-xs" /> New Floor
                </button>
              </div>

              <div className="twin-location-floor-list">
                {locations.map((loc) => {
                  const isActive = loc.id === activeLocationId;
                  const isInspected = loc.id === inspectedLocationId;
                  const rCount = loc.rooms?.length || 0;
                  const pAgentCount = loc.agents?.filter((ag) => ag.isPlaced === true || (ag.isPlaced !== false && ag.currentCol >= 0 && ag.currentRow >= 0)).length || 0;

                  return (
                    <div
                      key={loc.id}
                      className={`twin-location-floor-card ${isActive ? 'twin-location-floor-card--active' : isInspected ? 'twin-location-floor-card--inspected' : ''}`}
                    >
                      <div
                        onClick={() => {
                          onSelectLocation(loc.id);
                          setInspectedLocationId(loc.id);
                          setActiveTab('details');
                        }}
                        className="twin-location-floor-card-left"
                      >
                        <div className="twin-location-floor-icon-box">{loc.icon || '🏢'}</div>

                        <div className="twin-location-floor-info">
                          <div className="twin-location-floor-name-row">
                            <span className="twin-location-floor-name">{loc.name}</span>
                            {isActive && <span className="twin-location-floor-active-badge">Active</span>}
                          </div>
                          <span className="twin-location-floor-desc">{loc.description || `${loc.gridWidth}x${loc.gridHeight} tiles`}</span>
                          <div className="twin-location-floor-meta-row">
                            <span className="twin-location-floor-meta-item">
                              <Grid className="twin-icon-xxs twin-color-cyan" /> {loc.gridWidth}x{loc.gridHeight}
                            </span>
                            <span>•</span>
                            <span className="twin-location-floor-meta-item">
                              <Layers className="twin-icon-xxs twin-color-theme-accent" /> {rCount} zones
                            </span>
                            <span>•</span>
                            <span className="twin-location-floor-meta-item twin-location-floor-meta-item--emerald">
                              <Users className="twin-icon-xxs twin-color-emerald" /> {pAgentCount} agents
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="twin-location-floor-actions">
                        <button
                          type="button"
                          onClick={() => {
                            setInspectedLocationId(loc.id);
                            setActiveTab('details');
                          }}
                          className="twin-location-details-btn"
                          title="View details of this floor"
                        >
                          Details
                        </button>

                        {!isActive && (
                          <button
                            type="button"
                            onClick={() => {
                              onSelectLocation(loc.id);
                              setInspectedLocationId(loc.id);
                              showToast(`Floor "${loc.name}" selected as active`);
                            }}
                            className="twin-location-open-btn"
                          >
                            Open
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* === TAB 3: CREAR NUEVA UBICACIÓN === */}
          {activeTab === 'create' && (
            <div className="twin-location-section">
              <div className="twin-location-create-card">
                <span className="twin-location-create-section-label">1. Initial Template or Empty Grid</span>

                <div className="twin-location-template-grid">
                  <button
                    type="button"
                    onClick={() => {
                      setTemplateChoice('office');
                      setNewName('Floor 2 - Offices');
                      setNewIcon('🏢');
                      setNewType('office');
                    }}
                    className={`twin-location-template-btn ${templateChoice === 'office' ? 'twin-location-template-btn--office-active' : ''}`}
                  >
                    <span className="twin-location-template-emoji">🏢</span>
                    <div>
                      <div className="twin-location-template-name">Tech Office Floor</div>
                      <div className="twin-location-template-desc">14x14 with rooms, workstations, and cafeteria</div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setTemplateChoice('warehouse');
                      setNewName('Ground Floor - Logistics Warehouse');
                      setNewIcon('📦');
                      setNewType('warehouse');
                    }}
                    className={`twin-location-template-btn ${templateChoice === 'warehouse' ? 'twin-location-template-btn--warehouse-active' : ''}`}
                  >
                    <span className="twin-location-template-emoji">📦</span>
                    <div>
                      <div className="twin-location-template-name">Warehouse &amp; Logistics Bay</div>
                      <div className="twin-location-template-desc">16x12 with stock, docks, and racks</div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setTemplateChoice('lounge');
                      setNewName('Top Floor - Lounge & Executive Suite');
                      setNewIcon('🛋️');
                      setNewType('lounge');
                    }}
                    className={`twin-location-template-btn ${templateChoice === 'lounge' ? 'twin-location-template-btn--lounge-active' : ''}`}
                  >
                    <span className="twin-location-template-emoji">🛋️</span>
                    <div>
                      <div className="twin-location-template-name">Creative Lounge &amp; Executive</div>
                      <div className="twin-location-template-desc">12x12 with design pods, sofas, and relax area</div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setTemplateChoice('empty');
                      setNewName('New Open Space');
                      setNewIcon('📐');
                      setNewType('custom');
                    }}
                    className={`twin-location-template-btn ${templateChoice === 'empty' ? 'twin-location-template-btn--empty-active' : ''}`}
                  >
                    <span className="twin-location-template-emoji">📐</span>
                    <div>
                      <div className="twin-location-template-name">Empty Open Grid</div>
                      <div className="twin-location-template-desc">Build floors and walls from scratch</div>
                    </div>
                  </button>
                </div>
              </div>

              <div className="twin-location-create-card">
                <span className="twin-location-create-section-label">2. Floor Details</span>

                <div className="twin-location-form-grid3">
                  <div className="twin-location-form-field-name">
                    <label className="twin-location-edit-field-label">Floor Name:</label>
                    <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Floor 3 - R&D..." className="twin-location-edit-input" />
                  </div>

                  <div>
                    <label className="twin-location-edit-field-label">Icon / Emoji:</label>
                    <input type="text" value={newIcon} onChange={(e) => setNewIcon(e.target.value)} className="twin-location-edit-icon-input" maxLength={4} />
                  </div>
                </div>

                <div className="twin-location-edit-grid2">
                  <div>
                    <label className="twin-location-edit-field-label">Space Type:</label>
                    <select value={newType} onChange={(e) => setNewType(e.target.value as LocationType)} className="twin-location-edit-select">
                      {LOCATION_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.icon} {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="twin-location-edit-field-label">Description:</label>
                    <input type="text" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="e.g. Main Building, Floor 2..." className="twin-location-edit-input" />
                  </div>
                </div>

                <div className="twin-location-tz-field">
                  <label className="twin-location-tz-label-row">
                    <span className="twin-location-tz-label">
                      <Clock className="twin-icon-xxs twin-color-cyan" />
                      <span>Floor / Facility Timezone:</span>
                    </span>
                    <span className="twin-location-tz-offset">{getTimezoneOffsetDisplay(newTimezone)}</span>
                  </label>
                  <select value={newTimezone} onChange={(e) => setNewTimezone(e.target.value)} className="twin-location-tz-select">
                    {POPULAR_IANA_TIMEZONES.map((tz) => (
                      <option key={tz.iana} value={tz.iana}>
                        {tz.flag} {tz.name} ({getTimezoneOffsetDisplay(tz.iana)})
                      </option>
                    ))}
                  </select>
                </div>

                {templateChoice === 'empty' && (
                  <div className="twin-location-dims-row">
                    <div>
                      <label className="twin-location-edit-field-label">Width (Tiles):</label>
                      <input type="number" min={4} max={50} value={newWidth} onChange={(e) => setNewWidth(parseInt(e.target.value, 10) || 14)} className="twin-location-dims-input" />
                    </div>
                    <div>
                      <label className="twin-location-edit-field-label">Length (Tiles):</label>
                      <input type="number" min={4} max={50} value={newHeight} onChange={(e) => setNewHeight(parseInt(e.target.value, 10) || 14)} className="twin-location-dims-input" />
                    </div>
                  </div>
                )}
              </div>

              <div className="twin-location-create-footer">
                <button type="button" onClick={() => setActiveTab('details')} className="twin-location-create-cancel-btn">
                  Cancel
                </button>
                <button type="button" onClick={handleCreate} className="twin-location-create-submit-btn">
                  <Plus className="twin-icon-sm" />
                  <span>Build New Floor</span>
                </button>
              </div>
            </div>
          )}

          {/* === TAB 4: TRANSFERIR AGENTES === */}
          {activeTab === 'transfer' && (
            <div className="twin-location-section">
              <div className="twin-location-transfer-card">
                <span className="twin-location-transfer-title">
                  <ArrowRightLeft className="twin-icon-xs twin-color-theme-accent" />
                  <span>Move Agent to Another Floor / Location</span>
                </span>

                <p className="twin-location-transfer-desc">
                  Transfer AI agents between different building floors (e.g. move Alex DevOps from Floor 1 to the Logistics Warehouse).
                </p>

                <div className="twin-location-transfer-select-row">
                  <div>
                    <label className="twin-location-edit-field-label ">Source Floor:</label>
                    <select
                      value={transferFromLocId}
                      onChange={(e) => {
                        setTransferFromLocId(e.target.value);
                        setTransferAgentId('');
                      }}
                      className="twin-location-transfer-select"
                    >
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.icon} {loc.name} ({loc.agents?.length || 0} agents)
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="twin-location-edit-field-label">Destination Floor:</label>
                    <select value={transferToLocId} onChange={(e) => setTransferToLocId(e.target.value)} className="twin-location-transfer-select">
                      <option value="">-- Select Destination Floor --</option>
                      {locations
                        .filter((loc) => loc.id !== transferFromLocId)
                        .map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.icon} {loc.name}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="twin-location-agent-pick-label">Agent to Transfer ({sourceAgents.length} available at source):</label>
                  {sourceAgents.length === 0 ? (
                    <div className="twin-location-agent-pick-empty">No agents on this source floor.</div>
                  ) : (
                    <div className="twin-location-agent-pick-grid">
                      {sourceAgents.map((ag) => (
                        <button
                          key={ag.id}
                          type="button"
                          onClick={() => setTransferAgentId(ag.id)}
                          className={`twin-location-agent-pick-btn ${transferAgentId === ag.id ? 'twin-location-agent-pick-btn--selected' : ''}`}
                        >
                          <div className="twin-location-agent-pick-avatar" style={{ backgroundColor: ag.avatarShirtColor || '#8b5cf6' }}>
                            {ag.name.charAt(0)}
                          </div>
                          <div className="twin-location-agent-pick-text">
                            <span className="twin-location-agent-pick-name">{ag.name}</span>
                            <span className="twin-location-agent-pick-role">{ag.role}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="twin-location-transfer-footer">
                  <button type="button" onClick={handleExecuteTransfer} disabled={!transferAgentId || !transferToLocId} className="twin-location-transfer-submit-btn">
                    <ArrowRightLeft className="twin-icon-xs" />
                    <span>Execute Transfer</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* === FOOTER FIJO === */}
        <div className="twin-location-footer">
          <button type="button" onClick={onClose} className="twin-btn-cancel">
            Cancel
          </button>
        </div>
      </aside>

      {/* === MODAL DE CONFIRMACIÓN: ELIMINAR UBICACIÓN === */}
      <ConfirmDeleteModal
        isOpen={Boolean(locationToDelete)}
        onClose={() => setLocationToDelete(null)}
        onConfirm={() => {
          if (locationToDelete) {
            onDeleteLocation(locationToDelete.id);
            setLocationToDelete(null);
            showToast(`Floor "${locationToDelete.name}" deleted`);
          }
        }}
        title="Delete Location / Floor?"
        itemName={locationToDelete?.name}
        itemIcon={locationToDelete?.icon || '🏢'}
        itemType="location"
        description={
          locationToDelete
            ? `Are you sure you want to permanently delete floor "${locationToDelete.name}" (${locationToDelete.gridWidth}x${locationToDelete.gridHeight} tiles)? All items, zones, and elements on this floor will be lost.`
            : undefined
        }
        warningText="This action cannot be undone."
        confirmText="Delete Floor"
      />
    </>
  );
};